// components/Navbar.tsx
"use client";

import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";

export default function Navbar() {
  const t = useTranslations("nav");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/pricing", label: t("pricing") },
    { href: "/about",   label: t("about") },
    { href: "/contact", label: t("contact") },
  ];

  return (
    <nav>
        <div
          className="fixed top-0 start-0 end-0 z-50 transition-all duration-500 ease-in-out"
          style={{ padding: scrolled ? "10px 24px" : "0" }}
        >
        <div
          className={`
            flex items-center justify-between transition-all duration-500 ease-in-out
            ${scrolled ? "border border-base shadow-card" : ""}
          `}
          style={{
            height: scrolled ? "52px" : "64px",
            paddingInlineStart: scrolled ? "20px" : "32px",
            paddingInlineEnd: scrolled ? "20px" : "32px",
            borderRadius: scrolled ? "12px" : "0px",
            backgroundColor: scrolled
              ? "rgb(var(--bg-base) / 0.85)"
              : "rgb(var(--bg-base))",
            backdropFilter: scrolled ? "blur(16px)" : "none",
            WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
          }}
        >
          {/* Logo */}
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <img src="/layvers-logo.png" alt="Layvers" className="h-8 w-auto" />
            <span className="text-xl font-bold tracking-tight" style={{ color: "rgb(var(--brand-primary))" }}>
              Layvers
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.href.startsWith("#") ? (
               <a 
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-sec hover:text-fore transition-colors duration-200"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-sec hover:text-fore transition-colors duration-200"
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/signin"
              className="text-sm font-medium px-3 py-1.5 text-sec hover:text-fore transition-colors duration-200"
            >
              {t("login")}
            </Link>

            <Link
              href="/signup"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg
                         bg-accent text-white hover:opacity-90 shadow-accent transition-all duration-200"
            >
              {t("getStarted")}
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-fore"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
       <div className="fixed top-[74px] start-4 end-4 z-40 bg-base border border-base
                        backdrop-blur-xl rounded-2xl shadow-card px-4 py-4 flex flex-col gap-1 md:hidden">
          {navLinks.map((link) => (
            link.href.startsWith("#") ? (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="py-2.5 text-sm font-medium text-fore border-b border-base"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="py-2.5 text-sm font-medium text-fore border-b border-base"
              >
                {link.label}
              </Link>
            )
          ))}
          <div className="flex flex-col gap-2 pt-3">
            <Link
              href="/signin"
              onClick={() => setMobileOpen(false)}
              className="py-2.5 text-center text-sm font-medium border border-base rounded-xl text-fore"
            >
              {t("login")}
            </Link>
            <Link
              href="/signup"
              onClick={() => setMobileOpen(false)}
              className="py-2.5 text-center text-sm font-semibold text-white bg-accent rounded-xl"
            >
              {t("getStarted")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}